import Loader from "@components/Loader/Loader";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import FormField from "@components/Form/FormFields/FormFields";
import { NextContentfulImage } from "@components/Image";
import RichTextDisplay from "@components/RichTextDisplay/RichTextDisplay";
import SubmitButton from "@components/SubmitButton/SubmitButton";
import { ImageWrapperType } from "@customTypes/imageWrapper";
import { TinyMceRichText } from "@customTypes/TinyMceRichText";
import { useSettings } from "@utilities/context/settings";
import { useDevice } from "@utilities/react/get-device/get-device";
import { UserContext } from "../../context/user";
import {
  getPersonalDetails,
  subscribeThink,
  updateSubscribeThink,
} from "../../services/forms";

import { getBusinessUnit } from "@utilities/getBusinessUnit";
import setGADataLayer, {
  dataLayerEventKey,
  dataLayerStatusKey,
} from "@utilities/setGADataLayer";
import { THINKEMAIL_URL } from "../../constants";
import { CloseIcon } from "./CloseIcon";
import styles from "./SubscribeThink.module.scss";

// TODO: Fix SubscribeThinkProps interface
export interface SubscribeThinkProps {
  introText: string;
  verifyTitle: string;
  errorMessage: string;
  thankYouMessage: any;
  thankYouMessageTinyMce: TinyMceRichText;
  thankYouTitle: string;
  verifyMessage: any;
  verifyMessageTinyMce: TinyMceRichText;
  descriptionAboveForm: Document;
  description: any;
  descriptionTinyMce: TinyMceRichText;
  bannerImage: ImageWrapperType;
  bannerImageMobile: ImageWrapperType;
  emailLabelText: string;
  familyNameLabelText: string;
  firstNameLabelText: string;
}

interface Props {
  content: SubscribeThinkProps;
}

let firstInteract = 0;

export const SubscribeThink: React.FC<Props> = ({ content }) => {
  const {
    introText,
    verifyTitle,
    errorMessage,
    thankYouMessage,
    thankYouTitle,
    thankYouMessageTinyMce,
    verifyMessage,
    verifyMessageTinyMce,
    descriptionAboveForm,
    description,
    descriptionTinyMce,
    bannerImage,
    bannerImageMobile,
    emailLabelText,
    familyNameLabelText,
    firstNameLabelText,
  } = content;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { register, handleSubmit, formState, watch } = useForm({
    mode: "onBlur",
  });
  const { siteSettings } = useSettings();
  const { user } = useContext(UserContext);

  const router = useRouter();
  const { usertoken } = router.query;

  const [show, setShow] = useState(false);
  const [, setUrl] = useState("");
  const [activeStep, setActiveStep] = useState<
    | "subscribeScreen"
    | "verifyScreen"
    | "thankYouScreen"
    | "errorScreen"
    | "subscribeScreenLoggedIn"
  >("subscribeScreen");
  const [userPersonalDetails, setUserPersonalDetails] = useState<any>(null);
  const [hideForm, setHideForm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [CryptoJS, setCryptoJS] = useState(null);

  useEffect(() => {
    if (!dialogRef?.current) {
      return;
    }

    if (show && dialogRef?.current?.open !== true) {
      dialogRef?.current?.showModal();
    } else if (!show && dialogRef?.current?.open === true) {
      dialogRef?.current?.close();
    }
  }, [show, dialogRef?.current]);

  const onClose = () => {
    setShow(false);
    const pageURL = router["asPath"];
    router?.push(pageURL?.split("?")[0]);
  };

  const loadCryptoJS = async () => {
    if (!CryptoJS) {
      const Crypto = await import("crypto-js");
      setCryptoJS(Crypto);
      return Crypto;
    }
    return CryptoJS;
  };

  useEffect(() => {
    const loadLibrary = async () => {
      if (usertoken) {
        loadCryptoJS();
      }
    };
    loadLibrary();
  }, [usertoken]);

  const getUser = () => {
    const getUserData = async () => {
      try {
        const personalDetails = await getPersonalDetails();
        setUserPersonalDetails(personalDetails);
      } catch (_e) {
        setActiveStep("errorScreen");
      }
    };
    getUserData();
  };

  useEffect(() => {
    if (usertoken) {
      if (!CryptoJS) return;
      const submitData = async () => {
        const usertoken: string | undefined = router?.query
          ?.usertoken as string;
        const bytes = CryptoJS.AES.decrypt(
          usertoken?.replaceAll(" ", "+"),
          "w00tz0r"
        );
        const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

        decryptedData.isStayInformed = true;

        try {
          await subscribeThink(
            decryptedData,
            sessionStorage.getItem("crm_campaign") ||
              router?.query?.utm_campaign
          );
          setActiveStep("thankYouScreen");
        } catch (e) {
          console.log("error", e);
        }
      };

      submitData();
    }

    if (router["asPath"].split("#")[1] === "subscribeScreen") {
      const maintanancePage = siteSettings?.siteMaintenancePage?.slug;
      const isMaintananceModeOn = siteSettings?.turnOnSiteMaintenanceMode;
      if (isMaintananceModeOn) {
        router.push(`/${maintanancePage}`);
      }
      /*const checkApiStatus = async () => {
        const response = await checkApiHealth();
        if (response.statusCode != 200 || response.status === "Unhealthy") {
          router.push(`/${maintanancePage}`);
        }
      };
      checkApiStatus();*/

      if (user) {
        getUser();
      } else {
        setShow(true);
      }
      setUrl(document.URL);
    } else {
      setShow(false);
    }
  }, [router, user, CryptoJS]);

  useEffect(() => {
    if (usertoken) {
      setHideForm(true);
    }
  }, [usertoken]);

  useEffect(() => {
    if (userPersonalDetails) {
      showSubcribeScreenForLoggedInUser();
    }
  }, [userPersonalDetails]);

  const showSubcribeScreenForLoggedInUser = () => {
    setShow(true);
    setActiveStep("subscribeScreenLoggedIn");
  };

  const subscribeUser = async () => {
    try {
      setShow(true);
      setActiveStep("thankYouScreen");
      await updateSubscribeThink(
        { isStayInformed: true },
        sessionStorage.getItem("crm_campaign")
      );
    } catch (_e) {
      setActiveStep("errorScreen");
    }
  };

  useEffect(() => {
    const subscription = watch((_e, a) => {
      if (a.type === "change" && firstInteract < 1) {
        setGADataLayer({
          event: "formStart",
          formName: "subscribeThink",
        });
        firstInteract++;
        return () => subscription.unsubscribe();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    //set newsletter attempt
    if (formState.submitCount > 0) {
      setGADataLayer({
        data: {
          status: dataLayerStatusKey.SUBMIT_ATTEMPT,
        },
        _clear: true,
        event: dataLayerEventKey.NEWSLETTER,
      });

      //failed attempt
      const errorList = Object.keys(formState.errors)
        .map((key) => key)
        ?.join(",");
      if (errorList) {
        //set dataLayer
        setGADataLayer({
          data: {
            status: dataLayerStatusKey.SUBMIT_FAIL,
            form_field: errorList,
          },
          _clear: true,
          event: dataLayerEventKey.NEWSLETTER,
        });
      }
    }
  }, [formState.submitCount]);

  const formFields = [
    {
      propertyName: "firstName",
      validation: {
        isRequired: true,
        maxLength: 30,
        firstName: true,
      },
      labelText: firstNameLabelText,
      dataText: userPersonalDetails?.firstName,
    },
    {
      propertyName: "lastName",
      validation: {
        isRequired: true,
        lastName: true,
        stringValidator: true,
      },
      labelText: familyNameLabelText,
      dataText: userPersonalDetails?.lastName,
    },

    {
      propertyName: "emailAddress",
      validation: {
        isRequired: true,
        email: true,
        maxLength: 100,
      },
      labelText: emailLabelText,
      dataText: userPersonalDetails?.email,
    },
  ];

  const sendGAEvent = (event) => {
    setGADataLayer({
      event,
      formName: "subscribeThink",
      programmeCode: router?.query?.programmeTypeCode || "",
      businessUnit: getBusinessUnit(),
    });
  };

  useEffect(() => {
    if (activeStep === "thankYouScreen") {
      sendGAEvent("formComplete");
    }
  }, [activeStep]);

  const onSubmit = async (data) => {
    setLoading(true);
    const loadedCryptoJS = await loadCryptoJS();
    if (!loadedCryptoJS) return;

    const ciphertext = loadedCryptoJS.AES.encrypt(
      JSON.stringify(data),
      "w00tz0r"
    ).toString();

    // call to the Netlify Function
    sendGAEvent("formSubmit");
    try {
      const response = await fetch(THINKEMAIL_URL, {
        method: "POST",
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          emailAddress: data.emailAddress,
          token: ciphertext,
          crmCampaign: sessionStorage.getItem("crm_campaign"),
        }),
      });

      if (response.ok) {
        setActiveStep("verifyScreen");
      } else {
        setActiveStep("errorScreen");
      }
    } catch (_e) {
      setActiveStep("errorScreen");
    } finally {
      setLoading(false);
    }
  };

  const device = useDevice();
  const isMobile = device === "mobile";

  const CloseButton = () => (
    <a
      href={"/"}
      role="button"
      aria-label="Close dialog"
      onClick={(e) => {
        e.preventDefault();
        onClose();
      }}
      className={styles.cancel}
    >
      <CloseIcon className="w-10 h-10" />
    </a>
  );

  const LinkImageJSX = () => (
    <>
      <div aria-hidden className={styles["subscribe-think__image"]}>
        <NextContentfulImage
          src={
            !isMobile || (isMobile && !bannerImageMobile)
              ? bannerImage?.fields.url
              : bannerImageMobile?.fields.url
          }
          alt={
            !isMobile
              ? bannerImage?.fields.altText || ""
              : bannerImageMobile?.fields.altText || ""
          }
          width={900}
          height={!isMobile ? 100 : 50}
          style={{ height: "auto" }}
        />
      </div>
    </>
  );

  const isLoading = !router.query.entry && !router.query.usertoken;
  return (
    <dialog
      title="Subscribe to Think"
      ref={dialogRef}
      className={clsx(
        "component",
        "subscribeThink",
        show && styles.show,
        styles["subscribe-think"]
      )}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="wrapper subscribeThink">
        {activeStep === "subscribeScreen" && (!hideForm || isLoading) && (
          <div id="subscribeScreen" className={styles["overlay-content"]}>
            <div
              className={clsx(
                styles["subscribe-screen-content"],
                styles.popup,
                "wrapper"
              )}
            >
              <LinkImageJSX />
              <h4>{introText}</h4>
              {(isLoading && (
                <div className={styles.spinner}>
                  <Loader
                    color="#001e62"
                    size={80}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                  />
                </div>
              )) || (
                <form
                  method="POST"
                  onSubmit={handleSubmit(onSubmit)}
                  className={clsx(
                    styles.form,
                    "form",
                    styles["my-profile"],
                    "my-profile"
                  )}
                >
                  <RichTextDisplay
                    richText={descriptionAboveForm}
                    tinyMceRichText={null}
                  />
                  <div className={styles["fields-floated"]}>
                    {formFields.map(
                      (
                        { dataText, labelText, propertyName, validation },
                        index
                      ) => (
                        <div
                          className={clsx(
                            "form-row",
                            styles["form-row"],
                            styles["field-row"]
                          )}
                          key={`${labelText}${index}`}
                        >
                          <FormField
                            watch={watch}
                            validation={validation}
                            type="text"
                            register={register}
                            property={dataText}
                            placeholder={labelText}
                            name={propertyName || ""}
                            errors={formState.errors}
                          />
                        </div>
                      )
                    )}
                  </div>
                  <RichTextDisplay
                    richText={description}
                    tinyMceRichText={descriptionTinyMce}
                  />
                  <SubmitButton
                    loading={loading}
                    id="submitButton"
                    text="Subscribe"
                  />
                </form>
              )}
              <CloseButton />
            </div>
          </div>
        )}
        {activeStep === "subscribeScreenLoggedIn" && !hideForm && (
          <div id="subscribeScreen" className={styles["overlay-content"]}>
            <div
              className={clsx(
                styles["subscribe-screen-content"],
                styles.popup,
                "wrapper"
              )}
            >
              <LinkImageJSX />
              <h4>{introText}</h4>
              <form
                onSubmit={handleSubmit(subscribeUser)}
                className={clsx(
                  styles.form,
                  "form",
                  styles["my-profile"],
                  "my-profile"
                )}
              >
                <RichTextDisplay
                  richText={description}
                  tinyMceRichText={descriptionTinyMce}
                />
                <SubmitButton
                  loading={loading}
                  id="submitButton"
                  text="Subscribe"
                />
              </form>
              <CloseButton />
            </div>
          </div>
        )}
        {activeStep === "verifyScreen" && (
          <div id="verifyScreen" className={styles["overlay-content"]}>
            <div className={clsx(styles.popup, "wrapper")}>
              <LinkImageJSX />
              <h4>{verifyTitle}</h4>
              <RichTextDisplay
                richText={verifyMessage}
                tinyMceRichText={verifyMessageTinyMce}
              />
              <CloseButton />
            </div>
          </div>
        )}
        {activeStep === "thankYouScreen" && (
          <div id="thankYouScreen" className={styles["overlay-content"]}>
            <div className={clsx(styles.popup, "wrapper")}>
              <LinkImageJSX />
              <div className={styles["header-align"]}>
                <svg width="35" height="35" xmlns="http://www.w3.org/2000/svg">
                  <g fill="none" fillRule="evenodd">
                    <rect
                      stroke="#5CB300"
                      x=".5"
                      y=".5"
                      width="34"
                      height="34"
                      rx="17"
                    />
                    <path
                      stroke="#65C500"
                      strokeWidth="4"
                      strokeLinecap="square"
                      d="m9 20 6 5M26 12 15.75 24.75"
                    />
                  </g>
                </svg>

                <h4>{thankYouTitle}</h4>
              </div>
              <p>
                <RichTextDisplay
                  richText={thankYouMessage}
                  tinyMceRichText={thankYouMessageTinyMce}
                />
              </p>
              <CloseButton />
            </div>
          </div>
        )}
        {activeStep === "errorScreen" && (
          <div id="errorScreen" className={styles["overlay-content"]}>
            <div className={clsx(styles.popup, "wrapper")}>
              <LinkImageJSX />
              <p className={styles.error}>{errorMessage}</p>
              <CloseButton />
            </div>
          </div>
        )}
      </div>
    </dialog>
  );
};

export default SubscribeThink;
